import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addReport, setActiveReport, updateMessageFeedback } from "../../state/actions";
import ReactMarkdown from "react-markdown";
import ReportCard from "../ReportCard";
import "./Message.scss";
import Accordion from "../Accordion";
import ChartBlock from "../ChartBlock";
import TableBlock from "../TableBlock";
import SourcesPill from "../SourcesPill";
import MessageActions from "../MessageActions";
import QueryContextBar from "../QueryContextBar";
import { copyToClipboard } from "../../utils/copyToClipboard";

const Message = ({ msg, previousType, scrollToBottom, messageIndex = 0 }) => {
  const userDetail = useSelector((state) => state.userDetail);
  const userInitial = userDetail?.firstName?.charAt(0).toUpperCase() || "U";
  const senderType = msg.type || "You";
  const payload = { ...(msg.payload || {}) };
  const fallbackFields = [
    "text",
    "assumption",
    "link",
    "title",
    "description",
    "sources",
    "source_type",
    "grounding_sources",
    "chartConfig",
    "tableConfig",
    "report_id",
    "report_data",
    "report_status",
    "report_status_message",
    "report_title",
  ];

  fallbackFields.forEach((field) => {
    if (msg[field] !== undefined && payload[field] === undefined) {
      payload[field] = msg[field];
    }
  });

      const {
    text = "",
    assumption,
    link,
    title,
    description,
    sources = [],
    source_type,
    grounding_sources = [],
    chartConfig = {},
    tableConfig = { columns: [], rows: [] },
    report_id,
    report_data,
    report_status,
    report_status_message,
    report_title,
    query_params_data,
  } = payload;
  const message_type = msg.message_type || "text";
  const textContent = text || "";
  const assumptionText = assumption || textContent;
  const messageRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const messageElement = messageRef.current;
    if (messageElement) {
      setTimeout(() => {
        messageElement.classList.add("show");
      }, 100);
    }
  }, []);

  const containsMarkdown = (text) => /[*_~`[\]()]|https?:\/\//i.test(text);

  const isAssistant = senderType !== "You";

  const renderTextMessage = () => (
    <div className={senderType}>
      <div className="markupdiv">
        {senderType === "You" && (
          <div className="username-letter">
            <span>{userInitial}</span>
          </div>
        )}
        <div className="message-content">
          {containsMarkdown(textContent) ? (
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a target="_blank" rel="noopener noreferrer" {...props} />
                ),
              }}
            >
              {textContent}
            </ReactMarkdown>
          ) : (
            textContent
          )}
          {query_params_data && (
            <div className="query-params-inline">
              <QueryContextBar data={query_params_data} />
            </div>
          )}
          {sources && sources.length > 0 && message_type !== "sources" && (
            <SourcesPill sources={sources} />
          )}
        </div>
        {isAssistant && (
          <div className="message-actions-outer">
            <MessageActions
              traceId={msg.trace_id ?? null}
              copyText={textContent}
              initialRating={msg.feedback?.rating ?? null}
              onFeedbackChange={(rating) => dispatch(updateMessageFeedback(messageIndex, rating))}
              onCopy={copyToClipboard}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderAssumptionMessage = () => (
    <Accordion title="Assumptions" >
      <p>{assumptionText}</p>
    </Accordion>
  )

  const renderChartConfigMessage = () => (
    <div className="chat-block">
      <ChartBlock highchartsConfig={chartConfig || {}} />
    </div>
  );

  const renderTableMessage = () => (
    <div className="chat-block">
      <TableBlock columns={tableConfig?.columns || []} rows={tableConfig?.rows || []} />
    </div>
  );

  const renderReportMessage = () => (
    <div className="chat-block">
      <ReportCard
        report_title={report_title}
        report_status={report_status}
        report_status_message={report_status_message}
        onClick={() => {
          if (report_status === 'success') {
            const report = {
              id: report_id,
              title: report_title,
              data: report_data,
            };
            dispatch(addReport(report));
            dispatch(setActiveReport(report_id));
          }
        }}
      />
    </div>
  );

  const renderByType = () => {
    switch (message_type) {
      case "assumption":
        return renderAssumptionMessage();
      case "chart":
        return renderChartConfigMessage();
      case "table":
        return renderTableMessage();
      case "report":
        return renderReportMessage();
      default:
        return renderTextMessage();
    }
  };

  return (
    <li
      ref={messageRef}
      className={`msg-text ${senderType === "You" ? "align-right" : "align-left"}`}
    >
      {renderByType()}
    </li>
  );
};

export default Message;
